import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Contact.css';
import ContactHeroSection from '../components/hero/ContactHeroSection';
import ScrollReveal from '../components/premium/ScrollReveal';
import FloatingOrbs from '../components/premium/FloatingOrbs';
import ContactBenefits from '../components/premium/ContactBenefits';
import type { WaitlistForm } from '../types/waitlist';
import { initialWaitlistForm } from '../types/waitlist';
import { submitWaitlistToAirtable } from '../services/submitWaitlistToAirtable';
import { WAITLIST_ROLE_OPTIONS } from '../data/waitlistRoles';
import {
  getAllMissingFieldsWithSteps,
  getMissingFieldsForStep,
  getWaitlistTotalSteps,
  isWaitlistFormComplete,
} from '../utils/waitlistStepValidation';

const Contact: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = useState<WaitlistForm>(initialWaitlistForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [stepError, setStepError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location.hash !== '#lummo-demo-form') return;
    const el = document.getElementById('lummo-demo-form');
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash, location.pathname, isSubmitted]);

  const handleInputChange = (field: keyof WaitlistForm, value: string) => {
    setStepError('');
    setSubmitError('');
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayChange = (
    field: keyof WaitlistForm,
    value: string,
    checked: boolean
  ) => {
    setStepError('');
    setSubmitError('');
    setFormData((prev) => ({
      ...prev,
      [field]: checked
        ? [...(prev[field] as string[]), value]
        : (prev[field] as string[]).filter((item) => item !== value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStepError('');
    if (!isWaitlistFormComplete(formData)) {
      const missing = getAllMissingFieldsWithSteps(formData);
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(
          '[Lummo Waitlist] Contact: submit blocked — form incomplete (no request to Airtable). Missing:',
          missing
        );
      }
      setSubmitError(
        `Please complete all required fields: ${missing.join('; ')}.`
      );
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);
    if (typeof console !== 'undefined' && console.info) {
      console.info('[Lummo Waitlist] Contact: submitting…');
    }
    try {
      await submitWaitlistToAirtable(formData);
      if (typeof console !== 'undefined' && console.info) {
        console.info('[Lummo Waitlist] Contact: Airtable submit succeeded');
      }
      setIsSubmitted(true);
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[Lummo Waitlist] Contact: submit failed', err);
      }
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    const missing = getMissingFieldsForStep(currentStep, formData);
    if (missing.length > 0) {
      setStepError(`Please complete: ${missing.join(', ')}.`);
      return;
    }
    setStepError('');
    setCurrentStep((s) => (s < getWaitlistTotalSteps() ? s + 1 : s));
  };

  const prevStep = () => {
    setStepError('');
    setCurrentStep((s) => (s > 1 ? s - 1 : s));
  };

  const totalSteps = getWaitlistTotalSteps();
  const getStepProgress = () => (currentStep / totalSteps) * 100;

  if (isSubmitted) {
    return (
      <div className="contact-page">
        <section className="success-section">
          <FloatingOrbs />
          <div className="container">
            <ScrollReveal className="success-content">
              <div className="success-visual">
                <div className="success-checkmark">✓</div>
                <div className="success-particles">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className={`particle particle-${i}`}>✨</div>
                  ))}
                </div>
              </div>
              
              <h1>Welcome to the Future of Clinical Trials!</h1>
              <p>
                Thank you for joining our waitlist. You're now part of an exclusive group that will revolutionize clinical research in LATAM.
              </p>
              
              <div className="next-steps">
                <h3>What happens next:</h3>
                <div className="steps-list">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <h4>Custom Demo Preparation</h4>
                      <p>We'll review your information and prepare a personalized demonstration tailored to your specific needs and use cases.</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <h4>Priority Contact</h4>
                      <p>Our founders will personally reach out within 48 hours to schedule your exclusive demo session.</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <h4>Early Access Program</h4>
                      <p>You'll receive priority access to our beta platform with exclusive pricing and dedicated support.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="success-actions">
                <button 
                  className="primary success-button"
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData(initialWaitlistForm);
                    setCurrentStep(1);
                    setStepError('');
                    setSubmitError('');
                  }}
                >
                  <span>Register Another Company</span>
                </button>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Reemplazamos la sección waitlist-hero por nuestro componente ContactHeroSection */}
      <section className="hero-wrapper">
        <ContactHeroSection />
      </section>

      <section id="lummo-demo-form" className="waitlist-form-section contact-form-premium">
        <FloatingOrbs />
        <div className="container">
          <ScrollReveal>
          <div className="form-container">
            <div className="form-header">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${getStepProgress()}%` }}
                ></div>
              </div>
              
              <div className="step-indicator">
                <span className="current-step">Step {currentStep}</span>
                <span className="total-steps">of {totalSteps}</span>
              </div>
              
              <h2>
                {currentStep === 1 && 'Basic Information'}
                {currentStep === 2 && 'Company Details'}
                {currentStep === 3 && 'Trial needs & preferences'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="waitlist-form">
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        placeholder="your.email@company.com"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="company">Company *</label>
                      <input
                        type="text"
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        required
                        placeholder="Your company name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="role">Role *</label>
                      <select
                        id="role"
                        value={formData.role}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        required
                      >
                        <option value="">Select your role</option>
                        {WAITLIST_ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group full-width">
                      <label htmlFor="phone">Phone (Optional)</label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Company Details */}
              {currentStep === 2 && (
                <div className="form-step">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="companyType">Company Type *</label>
                      <select
                        id="companyType"
                        value={formData.companyType}
                        onChange={(e) => handleInputChange('companyType', e.target.value)}
                        required
                      >
                        <option value="">Select type</option>
                        <option value="pharma">Pharmaceutical Company</option>
                        <option value="biotech">Biotech</option>
                        <option value="cro">Contract Research Organization (CRO)</option>
                        <option value="academic">Academic Institution</option>
                        <option value="medical-device">Medical Device Company</option>
                        <option value="consulting">Consulting Firm</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="companySize">Company Size *</label>
                      <select
                        id="companySize"
                        value={formData.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value)}
                        required
                      >
                        <option value="">Select size</option>
                        <option value="startup">Startup (1-50 employees)</option>
                        <option value="small">Small (51-200 employees)</option>
                        <option value="medium">Medium (201-1000 employees)</option>
                        <option value="large">Large (1000+ employees)</option>
                      </select>
                    </div>
                    
                    <div className="form-group full-width">
                      <label htmlFor="headquarters">Headquarters *</label>
                      <select
                        id="headquarters"
                        value={formData.headquarters}
                        onChange={(e) => handleInputChange('headquarters', e.target.value)}
                        required
                      >
                        <option value="">Select country</option>
                        <option value="us">United States</option>
                        <option value="canada">Canada</option>
                        <option value="mexico">Mexico</option>
                        <option value="colombia">Colombia</option>
                        <option value="brazil">Brazil</option>
                        <option value="argentina">Argentina</option>
                        <option value="chile">Chile</option>
                        <option value="peru">Peru</option>
                        <option value="other-latam">Other LATAM</option>
                        <option value="europe">Europe</option>
                        <option value="asia">Asia</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Trial Needs */}
              {currentStep === 3 && (
                <div className="form-step">
                  <div className="form-section">
                    <h3>Trial Phases of Interest *</h3>
                    <div className="checkbox-grid">
                      {['Phase I', 'Phase II', 'Phase III', 'Phase IV', 'Real World Evidence'].map(phase => (
                        <label key={phase} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.trialPhase.includes(phase)}
                            onChange={(e) => handleArrayChange('trialPhase', phase, e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span>{phase}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h3>Therapeutic Areas *</h3>
                    <div className="checkbox-grid">
                      {['Oncology', 'Cardiology', 'Neurology', 'Diabetes', 'Immunology', 'Dermatology', 'Psychiatry', 'Ophthalmology', 'Other'].map(area => (
                        <label key={area} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.therapeuticAreas.includes(area)}
                            onChange={(e) => handleArrayChange('therapeuticAreas', area, e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span>{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="form-section">
                    <h3>LATAM Countries of Interest *</h3>
                    <div className="checkbox-grid">
                      {['Mexico', 'Colombia', 'Brazil', 'Argentina', 'Chile', 'Peru', 'Costa Rica', 'Panama', 'Other'].map(country => (
                        <label key={country} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.targetCountries.includes(country)}
                            onChange={(e) => handleArrayChange('targetCountries', country, e.target.checked)}
                          />
                          <span className="checkmark"></span>
                          <span>{country}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="expectedTrials">Expected Trials per Year *</label>
                      <select
                        id="expectedTrials"
                        value={formData.expectedTrials}
                        onChange={(e) => handleInputChange('expectedTrials', e.target.value)}
                        required
                      >
                        <option value="">Select quantity</option>
                        <option value="1-2">1-2 trials</option>
                        <option value="3-5">3-5 trials</option>
                        <option value="6-10">6-10 trials</option>
                        <option value="11-20">11-20 trials</option>
                        <option value="20+">20+ trials</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="timeframe">Timeframe to Start *</label>
                      <select
                        id="timeframe"
                        value={formData.timeframe}
                        onChange={(e) => handleInputChange('timeframe', e.target.value)}
                        required
                      >
                        <option value="">Select timeframe</option>
                        <option value="immediate">Immediate (1-3 months)</option>
                        <option value="short">Short term (3-6 months)</option>
                        <option value="medium">Medium term (6-12 months)</option>
                        <option value="long">Long term (12+ months)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="budgetRange">Annual Budget Range</label>
                      <select
                        id="budgetRange"
                        value={formData.budgetRange}
                        onChange={(e) => handleInputChange('budgetRange', e.target.value)}
                      >
                        <option value="">Select range</option>
                        <option value="under-50k">Under $50K</option>
                        <option value="50k-100k">$50K - $100K</option>
                        <option value="100k-250k">$100K - $250K</option>
                        <option value="250k-500k">$250K - $500K</option>
                        <option value="500k-1m">$500K - $1M</option>
                        <option value="over-1m">Over $1M</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="hearAboutUs">How did you hear about us?</label>
                      <select
                        id="hearAboutUs"
                        value={formData.hearAboutUs}
                        onChange={(e) => handleInputChange('hearAboutUs', e.target.value)}
                      >
                        <option value="">Select option</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="conference">Conference/Event</option>
                        <option value="referral">Referral</option>
                        <option value="google">Google Search</option>
                        <option value="industry-publication">Industry Publication</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="specificRequirements">Specific Requirements or Comments</label>
                    <textarea
                      id="specificRequirements"
                      value={formData.specificRequirements}
                      onChange={(e) => handleInputChange('specificRequirements', e.target.value)}
                      rows={4}
                      placeholder="Tell us about any specific requirements, necessary integrations, or additional comments..."
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                {(stepError || submitError) ? (
                  <p
                    className="waitlist-form__alert waitlist-form__alert--error"
                    role="alert"
                    aria-live="polite"
                  >
                    {stepError || submitError}
                  </p>
                ) : null}
                <div className="form-actions__buttons">
                {currentStep > 1 && (
                  <button type="button" onClick={prevStep} className="secondary">
                    <span>Previous</span>
                  </button>
                )}
                
                {currentStep < totalSteps ? (
                  <button type="button" onClick={nextStep} className="primary">
                    <span>Next</span>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="primary submit-button"
                    disabled={isSubmitting}
                  >
                    <span>{isSubmitting ? 'Sending…' : 'Submit Application'}</span>
                  </button>
                )}
                </div>
              </div>
            </form>
          </div>
          </ScrollReveal>
        </div>
      </section>

      <ContactBenefits />
    </div>
  );
};

export default Contact;

